import React, { useContext } from 'react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import { cs } from '@bbkAdminUtils/css-utils';
import Dropdown from '@bbkPricingPage/components/dropdown/dropdown';
import { PricingTableContext } from '@bbkPricingPage/context/pricing-table-context';
import { getFlagFromCurrency } from '@bbkPricingPage/lib/currencies';
import { type Currency } from '@bbkPricingPage/types/pricing-table-types';

const CurrencyDropdown: React.FC<{ currencies: Currency[] }> = ({
  currencies,
}) => {
  const { currency, setCurrency, showCurrencyDropdown } =
    useContext(PricingTableContext);

  if (!showCurrencyDropdown || currencies.length <= 1 || !currency) return null;

  return (
    <div>
      <Dropdown<Currency>
        id="currency-selector"
        itemKeyName={'currency_code'}
        items={currencies}
        selected={currency as Currency}
        onChange={(val) => setCurrency(val)}
        contentClassName="tw-shadow-md tw-overflow-hidden tw-bg-[var(--pricing-page-background-color)]
          tw-rounded-t-pricingtable-border-radius tw-rounded-b-pricingtable-border-radius
          tw-w-full"
        itemClassName="first:tw-rounded-t-pricingtable-border-radius last:tw-rounded-b-pricingtable-border-radius
          data-[highlighted]:tw-bg-[color-mix(in_srgb,var(--pricing-page-text-font-text-color)_10%,transparent)]"
        trigger={
          <div
            id="currency-selected"
            className={cs(
              'tw-inline-flex tw-w-full tw-items-center tw-justify-center tw-rounded-pricingtable-border-radius',
              'tw-font-medium tw-transition-all tw-duration-75',
              'tw-px-7 tw-py-4 tw-text-lg',
              '@sm:tw-px-4 @sm:tw-py-2 @sm:tw-text-sm',
              'focus:tw-outline-none',
              'tw-bg-[color-mix(in_srgb,var(--pricing-page-text-font-text-color)_10%,transparent)] tw-text-[color-mix(in_srgb,var(--pricing-page-text-font-text-color)_80%,transparent)]',
              'hover:tw-bg-[color-mix(in_srgb,var(--pricing-page-text-font-text-color)_15%,transparent)]'
            )}
          >
            <div className="tw-inline-flex tw-items-center tw-gap-1">
              <span className="tw-text-2xl tw-leading-none @sm:tw-text-lg">
                {getFlagFromCurrency(currency.currency_code)}
              </span>
              <span>{currency.currency_code}</span>
            </div>
            <ChevronDownIcon
              className="tw-ml-2 tw-h-5 tw-w-5"
              aria-hidden="true"
            />
          </div>
        }
        renderItem={(currency, isSelected) => (
          <span
            id={`currency-option-${currency.currency_code}`}
            className={cs(
              'tw-flex tw-items-center tw-gap-1 tw-truncate tw-text-[color-mix(in_srgb,var(--pricing-page-text-font-text-color)_80%,transparent)] hover:tw-cursor-pointer',
              'tw-py-5 tw-pl-4 tw-pr-8 tw-text-lg',
              '@sm:tw-py-2 @sm:tw-pl-3 @sm:tw-pr-7 @sm:tw-text-sm',
              isSelected
                ? 'tw-font-bold tw-text-[var(--pricing-page-text-font-text-color)]'
                : 'tw-font-normal'
            )}
          >
            <CheckIcon
              className={cs(
                'tw-h-4 tw-w-4 tw-fill-[var(--pricing-page-text-font-text-color)]',
                isSelected ? 'tw-visible' : 'tw-invisible'
              )}
            />
            <span className="tw-inline-flex tw-items-center tw-gap-1">
              <span className="tw-text-2xl @sm:tw-text-lg">
                {getFlagFromCurrency(currency.currency_code)}
              </span>
              {currency.currency_code}
            </span>
          </span>
        )}
        renderItemMobile={(currency) => (
          <>
            {currency.currency_code}{' '}
            {getFlagFromCurrency(currency.currency_code)}
          </>
        )}
      ></Dropdown>
    </div>
  );
};

export default CurrencyDropdown;
